// Real time-domain vocal DSP: Gate, De-esser, Auto-Tune (pitch snap)
const NOTE = { C: 0, "C#": 1, D: 2, "D#": 3, E: 4, F: 5, "F#": 6, G: 7, "G#": 8, A: 9, "A#": 10, B: 11 };

export function parseKeyRoot(key) {
  const m = (key || "C").split(" ")[0];
  return NOTE[m] ?? 0;
}

function gate(input, sr, thrDb = -45) {
  const thr = Math.pow(10, thrDb / 20);
  const out = new Float32Array(input.length);
  const aA = Math.exp(-1 / (0.005 * sr));
  const aR = Math.exp(-1 / (0.08 * sr));
  let env = 0, g = 0;
  for (let i = 0; i < input.length; i++) {
    const rect = Math.abs(input[i]);
    env = rect > env ? rect * (1 - aA) + env * aA : rect * (1 - aR) + env * aR;
    const target = env > thr ? 1 : 0;
    g = target > g ? g * aA + target * (1 - aA) : g * aR + target * (1 - aR);
    out[i] = input[i] * g;
  }
  return out;
}

function deEss(input, sr) {
  const rc = 1 / (2 * Math.PI * 5500);
  const dt = 1 / sr;
  const alpha = rc / (rc + dt);
  const high = new Float32Array(input.length);
  const env = new Float32Array(input.length);
  const envA = Math.exp(-1 / (0.003 * sr));
  let prevIn = 0, prevHigh = 0, e = 0;
  for (let i = 0; i < input.length; i++) {
    const h = alpha * (prevHigh + input[i] - prevIn);
    prevHigh = h; prevIn = input[i];
    high[i] = h;
    const rect = Math.abs(h);
    e = rect > e ? rect : e * envA + rect * (1 - envA);
    env[i] = e;
  }
  const thr = 0.06;
  const out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    let g = 1;
    if (env[i] > thr) g = thr / env[i];
    out[i] = (input[i] - high[i]) + high[i] * g;
  }
  return out;
}

function nearestScaleFreq(freq, rootPc) {
  const midi = 69 + 12 * Math.log2(freq / 440);
  const scale = [0, 2, 4, 5, 7, 9, 11].map((x) => (x + rootPc) % 12);
  let best = Math.round(midi), bestDist = 99;
  for (let m = Math.round(midi) - 3; m <= Math.round(midi) + 3; m++) {
    const pc = ((m % 12) + 12) % 12;
    if (scale.includes(pc)) {
      const d = Math.abs(m - midi);
      if (d < bestDist) { bestDist = d; best = m; }
    }
  }
  return 440 * Math.pow(2, (best - 69) / 12);
}

function detectPeriod(x, start, len, sr) {
  const minP = Math.max(2, Math.floor(sr / 900));
  const maxP = Math.min(len - 1, Math.floor(sr / 70));
  let e0 = 0;
  for (let i = 0; i < len; i++) { const v = x[start + i]; e0 += v * v; }
  if (e0 < 1e-5) return 0;
  let best = 0, bestLag = 0;
  for (let lag = minP; lag <= maxP; lag++) {
    let s = 0;
    for (let i = 0; i + lag < len; i++) s += x[start + i] * x[start + i + lag];
    if (s > best) { best = s; bestLag = lag; }
  }
  if (best / e0 < 0.3) return 0;
  return bestLag;
}

function medianPitch(x, sr) {
  const frame = 2048, hop = 2048, fr = [];
  for (let s = 0; s + frame < x.length; s += hop) {
    const p = detectPeriod(x, s, frame, sr);
    if (p > 0) fr.push(sr / p);
  }
  if (!fr.length) return 0;
  fr.sort((a, b) => a - b);
  return fr[Math.floor(fr.length / 2)];
}

function resampleTo(input, outLen) {
  const out = new Float32Array(outLen);
  const M = input.length;
  for (let i = 0; i < outLen; i++) {
    const pos = (i * (M - 1)) / (outLen - 1);
    const idx = Math.floor(pos), frac = pos - idx;
    out[i] = idx + 1 < M ? input[idx] * (1 - frac) + input[idx + 1] * frac : input[idx];
  }
  return out;
}

function fitLength(a, N) {
  if (a.length === N) return a;
  const o = new Float32Array(N);
  o.set(a.subarray(0, Math.min(a.length, N)));
  return o;
}

// Pitch-corrects a monophonic track to the nearest scale note (real resample-based shift, time-aligned).
function autoTune(input, sr, rootPc) {
  const med = medianPitch(input, sr);
  if (med <= 0) return input;
  const target = nearestScaleFreq(med, rootPc);
  const p = target / med;
  if (Math.abs(p - 1) < 0.004) return input;
  const N = input.length;
  const shifted = resampleTo(input, Math.round(N / p));
  return fitLength(shifted, N);
}

export async function processTrackFx(ctx, srcBuffer, opts, keyRoot) {
  await new Promise((r) => setTimeout(r, 15));
  let data = Float32Array.from(srcBuffer.getChannelData(0));
  const sr = srcBuffer.sampleRate;
  if (opts.gate) data = gate(data, sr);
  if (opts.deEsser) data = deEss(data, sr);
  if (opts.autoTune) data = autoTune(data, sr, keyRoot);
  const out = ctx.createBuffer(1, data.length, sr);
  out.copyToChannel(data, 0);
  return out;
}
